import React from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

const BRAND_GREEN = '#477C2E';

const GRADIENT_OVERLAY =
  'linear-gradient(105deg, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.22) 45%, rgba(0,0,0,0.12) 65%, rgba(0,0,0,0.28) 100%)';

function resolveNavTarget(link, navigate) {
  const raw = (link || '').trim();
  if (!raw) return;
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    window.location.href = raw;
    return;
  }
  navigate(raw.startsWith('/') ? raw : `/${raw}`);
}

/**
 * Full-width hero with background image, light gradient, and left-aligned content (home slider style).
 */
const ImageHeroBanner = ({
  subtitle,
  title,
  description,
  backgroundImage,
  buttonText,
  buttonLink,
  onButtonClick,
  children,
  minHeight = { xs: 420, sm: 500, md: 560 },
  maxHeight = { md: 640 },
}) => {
  const navigate = useNavigate();
  const bgUrl = backgroundImage || '';

  const handleButton = () => {
    if (onButtonClick) {
      onButtonClick();
      return;
    }
    if (buttonLink) resolveNavTarget(buttonLink, navigate);
  };

  return (
    <>
      <Box
        component="section"
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '100vw',
          minHeight,
          height: { xs: 'auto', md: 'min(70vh, 640px)' },
          maxHeight,
          overflow: 'hidden',
          bgcolor: '#1a1a1a',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: bgUrl ? `url(${bgUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: GRADIENT_OVERLAY,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.22) 0%, transparent 50%)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            minHeight: 'inherit',
            display: 'flex',
            alignItems: 'center',
            px: { xs: 2.5, sm: 3 },
          }}
        >
          <Box sx={{ py: { xs: 6, md: 8 }, maxWidth: { xs: '100%', sm: 640, md: 720 } }}>
            {subtitle ? (
              <Typography
                sx={{
                  fontFamily: '"Caveat", cursive',
                  fontWeight: 700,
                  fontSize: { xs: 24, sm: 28, md: 34 },
                  lineHeight: 1.1,
                  color: 'rgba(255,255,255,0.95)',
                  mb: { xs: 1.5, md: 2 },
                  textShadow: '0 2px 12px rgba(0,0,0,0.25)',
                }}
              >
                {subtitle}
              </Typography>
            ) : null}

            {title ? (
              <Typography
                component="h1"
                sx={{
                  fontFamily: '"Nunito", sans-serif',
                  fontWeight: 900,
                  fontSize: { xs: 30, sm: 40, md: 52 },
                  lineHeight: { xs: 1.2, md: 1.15 },
                  color: '#fff',
                  mb: description || buttonText || children ? { xs: 2, md: 3 } : 0,
                  textShadow: '0 4px 24px rgba(0,0,0,0.35)',
                }}
              >
                {title}
              </Typography>
            ) : null}

            {description ? (
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: { xs: 15, md: 17 },
                  lineHeight: 1.7,
                  color: 'rgba(255,255,255,0.88)',
                  maxWidth: 600,
                  mb: buttonText || children ? { xs: 2.5, md: 3 } : 0,
                  textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                }}
              >
                {description}
              </Typography>
            ) : null}

            {(buttonText || children) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                {buttonText ? (
                  <Button
                    variant="contained"
                    onClick={onButtonClick ? handleButton : undefined}
                    component={buttonLink && !onButtonClick ? RouterLink : 'button'}
                    to={
                      buttonLink && !onButtonClick
                        ? buttonLink.startsWith('/')
                          ? buttonLink
                          : `/${buttonLink}`
                        : undefined
                    }
                    sx={{
                      height: { xs: 48, md: 52 },
                      borderRadius: '999px',
                      backgroundColor: BRAND_GREEN,
                      color: '#fff',
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 600,
                      fontSize: { xs: 13, md: 14 },
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      px: { xs: 3, md: 4 },
                      boxShadow: '0 8px 28px rgba(71, 124, 46, 0.45)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      '&:hover': {
                        backgroundColor: '#3d6a28',
                        boxShadow: '0 12px 32px rgba(71, 124, 46, 0.55)',
                      },
                    }}
                  >
                    {buttonText}
                  </Button>
                ) : null}
                {children}
              </Box>
            )}
          </Box>
        </Container>
      </Box>
      <Box sx={{ height: 4, bgcolor: '#EEECE1' }} />
    </>
  );
};

export default ImageHeroBanner;
